// @flow

/**
 * Decorate the Price component to get the same style and vat number display
 * condition...
 */

import * as React from 'react';

import memoize from 'memoize-one';

import { useTranslator, type TranslatorProps } from '@gandi/translate';
import { Text } from '@gandi/ui';

import { useAppSelector } from 'src/hooks/useAppSelector';
import { selectCurrentBU } from 'src/modules/organization/selectors';

import styles from './Price.scss';

type TPrice = ?(number | string);

export type Props = {
  currency?: string,
  priceCrossedOut?: TPrice,
  price?: TPrice,
  showExcludeVatMention?: boolean,
  notAvailable?: boolean,
  // Note that in order to allow the extraction of translations, priceInfo
  // can not be dynamically translated in this component.
  // You have to pass a translated string.
  priceInfo?: string,
  inline?: boolean,
  subtitle?: string,
  singleLine?: boolean,
};

const isValid = (price: TPrice): implies price is number =>
  Number.isFinite(price);

const getPriceInfo = memoize(
  (
    priceInfo?: string,
    __: TranslatorProps['__'],
    currentBU: void | number,
    showExcludeVatMention: ?boolean = false,
  ) => {
    const pricesInfos = [];

    if (priceInfo) {
      pricesInfos.push(priceInfo);
    }

    if (showExcludeVatMention && currentBU !== 3) {
      pricesInfos.push(__('excl. VAT'));
    }

    return pricesInfos.join(' - ');
  },
);

export default function Price({
  inline,
  notAvailable,
  price,
  priceCrossedOut,
  priceInfo,
  currency,
  showExcludeVatMention,
  subtitle,
  singleLine,
}: Props): React.MixedElement {
  const { __, formatPrice } = useTranslator();
  const currentBU = useAppSelector(selectCurrentBU);

  if (notAvailable || !isValid(price)) {
    return (
      <div>
        <Text size="base" weight="semibold" color="danger">
          {__('Price unavailable')}
        </Text>
      </div>
    );
  }

  const priceInfos = getPriceInfo(
    priceInfo,
    __,
    currentBU,
    showExcludeVatMention,
  );
  const size = inline ? 'small' : 'base';
  const weight = inline ? 'regular' : 'semibold';

  return (
    <div className={styles.container}>
      <div
        className={`${styles.pricePeriodContainer}  ${priceInfos || singleLine ? styles['single-line'] : ''}`}
      >
        {!!priceCrossedOut && priceCrossedOut !== price && (
          <>
            <Text
              tagName="del"
              size={size}
              weight={weight}
              decoration="line-through"
              className={styles.priceCrossedOut}
            >
              &nbsp;
              {formatPrice(Number(priceCrossedOut), currency)}
              &nbsp;
            </Text>{' '}
            &nbsp;
          </>
        )}
        <Text size={size} weight={weight}>
          {formatPrice(Number(price), currency)}
        </Text>
        <p className={styles.periodPrice}>{subtitle}</p>
      </div>

      {priceInfos && <p className={styles.subInfos}>({priceInfos})</p>}
    </div>
  );
}
